<template>
    <div>
        <TipTap
            v-model="editable_content.question"
            :menu_items="['blank', 'bold', 'italic', 'underline', 'bullet_list', 'ordered_list', 'image', 'edit_html']"
            :editable_content="editable_content"
            @input="handleTipTapUpdate"
        />

        <b-form-group
            v-if="editable_content.blanks && editable_content.blanks.length"
            label="Blanks"
            label-for="blanks"
        >
            <b-list-group>
                <b-list-group-item v-for="(blank, index) in editable_content.blanks" :key="index">

                    <span
                        v-b-toggle="'blank' + blank.blank_id"
                        class="collapse-icon text-dark"
                    >
                        <span class="open">
                            <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                        </span>
                        <span class="closed">
                            <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                        </span>
                        <span class="mx-1" :class="{'text-danger': validating && !blank.answer}">{{blank.blank_id}}</span>
                    </span>

                    <b-collapse :id="'blank' + blank.blank_id">
                        <b-form-group
                            v-if="!blank.options || blank.options && !blank.options.length && blank.is_open_response !== true"
                            label="Answer"
                            label-for="answer"
                        >
                            <b-form-input v-model="blank.answer" :state="validating && !blank.answer ? false : null" />

                            <b-form-invalid-feedback :state="validating && !blank.answer ? false : null && !blank.is_open_response">                                
                                Answer is required
                            </b-form-invalid-feedback>
                        </b-form-group>

                        <b-form-group
                            label-for="options"
                            class="mb-0"
                        >
                            <label v-if="!blank.is_open_response">
                                Options <sup><font-awesome-icon :icon="['fad', 'info-circle']" content="If you want the student to select the answer from a list of options" class="text-info" v-tippy /></sup>
                            </label>

                            <b-alert
                                v-if="!blank.is_open_response"
                                variant="warning"
                                :show="!blank.options || blank.options && !blank.options.length"
                                class="mb-2"
                            >
                                0 Options
                            </b-alert>

                            <div v-if="blank.options && blank.options.length && !blank.is_open_response" class="mb-2">
                                <div v-for="(option, index) in blank.options" :key="index" class="mb-1">
                                    <b-input-group>
                                        <b-input-group-prepend v-if="question.type != 'open_response_fill_in_the_blank' || blank.is_open_response !== true" is-text>
                                            <b-form-radio
                                                v-model="blank.answer"
                                                :value="option.option"
                                                :state="validating && blank.answer == null ? false : null"
                                                :disabled="!option.option"
                                                class="mr-n2"
                                            >
                                                <span class="sr-only"></span>
                                            </b-form-radio>
                                        </b-input-group-prepend>
                                        <b-input-group-prepend  v-else is-text >
                                            <font-awesome-icon :icon="['far', 'pen']" />
                                        </b-input-group-prepend>
                                        <b-form-input
                                            v-model="option.option"
                                            :state="validating && !option.option ? false : null"
                                            v-focus
                                        />
                                        <b-input-group-append>
                                            <b-btn variant="danger" @click.prevent="removeOption(blank, index)">
                                                <font-awesome-icon :icon="['far', 'minus']" />
                                            </b-btn>
                                        </b-input-group-append>
                                    </b-input-group>

                                    <b-form-invalid-feedback :state="validating && !option.option ? false : null">
                                        Option text is required
                                    </b-form-invalid-feedback>
                                </div>
                                <b-form-invalid-feedback :state="validating && blank.answer == null ? false : null ">
                                    Answer is required
                                </b-form-invalid-feedback>
                            </div>

                            <label v-if="blank.is_open_response === true">
                                Suggested Answers <sup><font-awesome-icon :icon="['fad', 'info-circle']" content="Teachers can use the suggested answers when evaluating student answers." class="text-info" v-tippy /></sup>
                            </label>

                            <div v-if="blank.is_open_response" class="mb-2">
                                <div class="mb-1">
                                    <b-input-group>
                                        <b-input-group-prepend is-text >
                                            <font-awesome-icon :icon="['far', 'pen']" />
                                        </b-input-group-prepend>
                                        <b-form-input
                                            v-model="blank.suggested_answers"
                                            style="background-color: orange; opacity: 0.5; color: black;"
                                            :state="validating && !blank.suggested_answers ? false : null"
                                            v-focus
                                        />
                                        
                                    </b-input-group>

                                </div>
                            </div>

                            <b-btn v-if="!blank.is_open_response" size="sm" variant="outline-primary" @click.prevent="addOption(blank)">
                                <font-awesome-icon :icon="['fad', 'plus-square']" /> Add Option
                            </b-btn>
                            <b-form-checkbox
                                class="float-right"
                                v-model="blank.is_open_response"
                                :checked="blank.is_open_response === true"
                                switch
                            >
                                Answers Will Vary
                            </b-form-checkbox>

                            <b-form-group
                                v-if="blank.is_open_response"
                                label="Open Response Limit"
                                label-for="open_response_limit"
                                class="mt-2"
                            >
                                <b-form-select
                                    id="open_response_limit"
                                    v-model="blank.open_response_limit"
                                    :options="openResponseLimits"
                                    :state="validating && !blank.open_response_limit ? false : null"
                                />
                            </b-form-group>

                        </b-form-group>
                    </b-collapse>
                </b-list-group-item>
            </b-list-group>
        </b-form-group>

        <b-form-invalid-feedback :state="validating && blankWithoutAnswer ? false : null && question.type != 'open_response_fill_in_the_blank'" class="mb-3">
            Answer is required for all blanks
        </b-form-invalid-feedback>

        <b-form-invalid-feedback class="mb-2" :state="validating && (!editable_content.blanks || editable_content.blanks && !editable_content.blanks.length) ? false : null">
            At least 1 blank is required
        </b-form-invalid-feedback>

    </div>
</template>

<style lang="scss">

</style>

<style lang="scss" scoped>
.prompt {
    min-height: 6rem;
}
</style>
<style>
    .ProseMirror table {
    width: 100%;
}
</style>

<script>
import { mapState } from 'vuex'

export default {
    data: () => ({
        options: [],
        editor: null,
        editable_content: {},
        selected_text: null
    }),
    props: [
        'question'
    ],
    components: {
        TipTap: () => import('@/components/tiptap/TipTap'),
    },
    computed: {
        ...mapState({
            validating: state => state.utilities.validating
        }),
        openResponseLimits () {
            return [
                { value: 50, text: '50' },
                { value: 500, text: '500' }
            ]
        },
        blankWithoutAnswer () {
            let blankWithoutAnswer = false

            if (this.editable_content.blanks) {
                _.forEach(this.editable_content.blanks, blank => {
                    if (!blank.answer) {
                        blankWithoutAnswer = true
                    }
                })
            }

            return blankWithoutAnswer
        }
    },
    watch: {
        question: {
            handler (question) {
                if (
                    question &&
                    question.content
                ) {
                    this.editable_content = question.content
                }
                
            },
            deep: true,
            immediate: true
        },
        editable_content: {
            handler (editable_content) {
                if (editable_content) {
                    this.$set(this.question, 'content', editable_content)

                    _.forEach(editable_content.blanks, blank => {
                        if (blank.options) {
                            this.$set(blank, 'options', blank.options)
                        }
                    })
                }
            },
            deep: true
        },
    },
    methods: {
        addOption (blank) {
            if (!blank.options) {
                this.$set(blank, 'options', [])
            }

            if (
                blank.answer &&
                !blank.options.length
            ) {
                blank.options.push({
                    option: blank.answer
                })
            } else {
                blank.options.push({})
            }
        },
        removeOption (blank, index) {
            blank.options.splice(index, 1)
        },
        optionWithValue (blank) {
            let optionWithValue = false

            _.forEach(blank.options, option => {
                if (option.option) {
                    optionWithValue = true
                }
            })

            return optionWithValue
        },
        handleTipTapUpdate(question) {
            if (question) {
                this.$set(this.editable_content, 'question', question)

                let parser = new DOMParser()
                let html = parser.parseFromString(question, 'text/html')

                let blanksCollection = html.getElementsByTagName('blank')

                let blanksArrayMap = _.map(blanksCollection, blank => {
                    let blank_id = blank.getAttribute('blank_id')

                    let selected_text = blank.getAttribute('answer')

                    if (!this.editable_content.blanks) {
                        this.$set(this.editable_content, 'blanks', [])
                    }

                    if (!_.find(this.editable_content.blanks, {blank_id: blank_id})) {
                        let suggested_answers = '';
                        let options = [{ option: selected_text }];

                        this.editable_content.blanks.push({
                            blank_id: blank_id,
                            options: options,
                            suggested_answers: suggested_answers,
                            is_open_response: false,
                            open_response_limit: 50,
                        })
                    }

                    return {
                        blank_id: blank_id
                    }
                })

                if (
                    this.editable_content.blanks &&
                    this.editable_content.blanks.length
                ) {
                    _.forEach(this.editable_content.blanks, (blank, index) => {
                        let blank_id = null

                        if (
                            blank &&
                            blank.blank_id
                        ) {
                            blank_id = blank.blank_id
                        }

                        if (!_.find(blanksArrayMap, {blank_id: blank_id})) {
                            this.editable_content.blanks.splice(index, 1)
                        }
                    })
                } else {
                    this.$set(this.editable_content, 'blanks', [])
                }
            } else {
                this.$set(this.editable_content, 'blanks', [])
            }
        }
    },
}
</script>
